import {omit} from 'lodash';
import {GridConfig, GridModel} from '@xh/hoist/cmp/grid';
import {HoistModel, managed, XH} from '@xh/hoist/core';
import {PanelModel} from '@xh/hoist/desktop/cmp/panel';
import {action, bindable, observable} from '@xh/hoist/mobx';
import {Icon} from '@xh/hoist/icon';
import {fmtTime, numberRenderer, priceRenderer} from '@xh/hoist/format';
import {wait} from '@xh/hoist/promise';
import {ConfigurableGridModel} from '../../../../cmp/gridSpec/ConfigurableGridModel';
import {NotificationMessage} from '../../../../data/MessageTypes';
import {Order, OrderStatusEnum} from '../../../../data/DataTypes';
import {ordersBaseColumn, ordersCapabilities, ordersCatalog, ordersDefaultSpec, ordersStoreFields} from './OrdersGridSpec';

export class WorkingOrdersModel extends HoistModel {
    private prefKey = 'portfoliosTabState';
    @observable accessor portfolioId: number;
    @observable accessor alswaysShowFills = false;
    @bindable accessor filterText: string;
    /** Owns the working orders grid and the filled orders grid linked to its spec. */
    @managed configurableGridModel = this.createConfigurableGridModel();
    workingOrdersModel = this.configurableGridModel.gridModel;
    completedOrdersModel = this.configurableGridModel.createLinkedGrid({gridConfig: this.gridConfig()});
    @managed fillsModel = this.createFillsModel();
    @managed ordersPanelModel = new PanelModel({
        defaultSize: 200,
        collapsible: true,
        resizable: true,
        minSize: 30,
        side: 'bottom',
        persistWith: {prefKey: this.prefKey, path: 'workingOrdersPanelSize'}
    });
    @managed completedOrdersPanelModel = new PanelModel({
        collapsible: true,
        resizable: true,
        defaultSize: 200,
        side: 'bottom',
        showHeaderCollapseButton: false,
        showSplitterCollapseButton: false,
        persistWith: {prefKey: this.prefKey, path: 'completedOrdersPanelSize'}
    });
    @managed fillsPanelModel = new PanelModel({
        side: 'right',
        defaultSize: 335,
        collapsible: true,
        resizable: false,
        showHeaderCollapseButton: false,
        showSplitterCollapseButton: false,
        persistWith: {prefKey: this.prefKey, path: 'fillsPanelSize'}
    });

    constructor() {
        super();

        this.addReaction({
            track: () => this.portfolioId,
            run: () => this.subscribeToOrdersAsync()
        });

        this.addReaction({
            track: () => this.filterText,
            run: () => this.filterOrders()
        });

        this.addReaction({
            track: () => this.workingOrdersModel.selectedId,
            run: (selectedId: string) => {
                const selectedRecord = selectedId ? this.workingOrdersModel.store.getById(selectedId) : null;
                if (selectedRecord) {
                    this.completedOrdersModel.clearSelection();
                    wait().then(() => {
                        this.fillsPanelModel.setCollapsed(false);
                        this.fillsModel.loadData(selectedRecord.data.fills || []);
                    });
                } else {
                    this.fillsModel.clear();
                    if (!this.alswaysShowFills) {
                        this.fillsPanelModel.setCollapsed(true);
                    }
                }
            }
        });

        this.addReaction({
            track: () => this.completedOrdersModel.selectedId,
            run: (selectedId: string) => {
                const selectedRecord = selectedId ? this.completedOrdersModel.store.getById(selectedId) : null;
                if (selectedRecord) {
                    this.workingOrdersModel.clearSelection();
                    wait().then(() => {
                        this.fillsPanelModel.setCollapsed(false);
                        this.fillsModel.loadData(selectedRecord.data.fills || []);
                    });
                } else {
                    this.fillsModel.clear();
                    if (!this.alswaysShowFills) {
                        this.fillsPanelModel.setCollapsed(true);
                    }
                }
            }
        });

        XH.messageHub.subscribe('orders', this.xhId, message => this.onOrdersMessage(message));

        const settings = XH.getPref(this.prefKey);
        this.alswaysShowFills = settings?.alwaysShowFills || false;
    }

    @action
    async setPortfolio(portfolioId: number) {
        this.portfolioId = portfolioId;
    }

    @action
    toggleCompleted() {
        this.completedOrdersPanelModel.setCollapsed(!this.completedOrdersPanelModel.collapsed);
    }

    @action toggleAlwaysShowFills() {
        this.alswaysShowFills = !this.alswaysShowFills;
        if (this.alswaysShowFills) {
            this.fillsPanelModel.setCollapsed(false);
        }

        const settings = XH.getPref(this.prefKey);
        XH.setPref(this.prefKey, {...settings, alwaysShowFills: this.alswaysShowFills});
    }

    private async subscribeToOrdersAsync() {
        const orders = await XH.orderService.fetchOrdersForPortfolioAsync(this.portfolioId);
        this.workingOrdersModel.loadData(orders.filter(o => this.isLive(o)));
        this.completedOrdersModel.loadData(orders.filter(o => !this.isLive(o)));
    }

    @action
    private onOrdersMessage(message: NotificationMessage) {
        switch (message.envelope.eventType) {
            case 'UPDATE':
                this.updateOrder(message.payload);
                break;
            case 'RELOAD':
                this.workingOrdersModel.clear();
                break;
        }
    }

    private updateOrder(order: Order) {
        if (order.portfolioId !== this.portfolioId) return;

        const isSelected = order.id === (this.workingOrdersModel.selectedId ?? this.completedOrdersModel.selectedId);
        if (this.isLive(order)) {
            this.workingOrdersModel.updateData([order]);
        } else {
            const wasWorking = !!this.workingOrdersModel.store.getById(order.id);
            if (wasWorking) this.workingOrdersModel.updateData({remove: [order.id]});
            this.completedOrdersModel.updateData([order]);

            // Follow a selected order into the completed grid, which shows its fills from there
            if (isSelected && wasWorking) {
                this.completedOrdersModel.selectAsync(order.id);
                return;
            }
        }

        // Selection reactions only load fills when the selection changes, so refresh them as fills arrive
        if (isSelected) this.fillsModel.loadData(order.fills ?? []);
    }

    private isLive(order: Order): boolean {
        const isLive =
            order.orderStatus == OrderStatusEnum.Open ||
            order.orderStatus == OrderStatusEnum.OpenPartialFill ||
            order.orderStatus == OrderStatusEnum.CancelPending;

        return isLive;
    }

    private filterOrders() {
        const filterText = this.filterText?.toLowerCase();
        if (!filterText) {
            this.workingOrdersModel.store.clearFilter();
            this.completedOrdersModel.store.clearFilter();
        } else {
            this.workingOrdersModel.store.setFilter(record => record.data.ticker.toLowerCase().includes(filterText));
            this.completedOrdersModel.store.setFilter(record => record.data.ticker.toLowerCase().includes(filterText));
        }
    }

    /** The working orders grid, whose spec also sets the columns of the filled orders grid. */
    private createConfigurableGridModel(): ConfigurableGridModel {
        return new ConfigurableGridModel({
            title: 'Orders',
            catalog: ordersCatalog,
            capabilities: ordersCapabilities,
            defaultSpec: ordersDefaultSpec,
            initialSpec: XH.getPref(this.prefKey)?.ordersGridSpec,
            // the grids' GridModel state from before the spec isn't carried over
            persistCallback: ordersGridSpec =>
                XH.setPref(this.prefKey, {...omit(XH.getPref(this.prefKey), 'workingOrdersGrid', 'completedOrdersGrid'), ordersGridSpec}),
            baseColumn: ordersBaseColumn,
            gridConfig: {
                ...this.gridConfig(),
                contextMenu: [
                    {
                        text: 'Approve Order',
                        icon: Icon.checkSquare(),
                        hidden: XH.entitlementService.isShadow,
                        intent: 'success',
                        actionFn: ({record}) => XH.orderService.approveOrderAsync(record.data.id)
                    },
                    {
                        text: 'Cancel Order',
                        icon: Icon.x(),
                        hidden: XH.entitlementService.isShadow,
                        intent: 'danger',
                        actionFn: ({record}) => XH.orderService.cancelOrderAsync(record.data.id)
                    },
                    '-',
                    ...GridModel.defaults.contextMenu
                ]
            }
        });
    }

    /** Store and column defaults of both order grids. Columns, sort and size come from the spec. */
    private gridConfig(): Omit<GridConfig, 'columns' | 'sortBy' | 'groupBy'> {
        return {
            store: {fields: ordersStoreFields},
            colDefaults: {
                sortable: false,
                resizable: false,
                movable: false
            },
            sizingMode: 'compact'
        };
    }

    private createFillsModel(): GridModel {
        return new GridModel({
            persistWith: {prefKey: this.prefKey, path: 'fillsGrid'},
            sortBy: 'id',
            colDefaults: {
                sortable: false,
                resizable: false,
                movable: false
            },
            colChooserModel: false,
            showCellFocus: false,
            sizingMode: 'compact',
            columns: [
                {field: 'id', hidden: true},
                {field: 'venue', width: 65, cellClass: 'order-muted'},
                {field: 'quantity', headerName: 'Qty', align: 'right', width: 70, renderer: numberRenderer({precision: 0})},
                {
                    field: 'price',
                    align: 'right',
                    width: 90,
                    renderer: priceRenderer({precision: 2, prefix: '$'})
                },
                {
                    field: 'created',
                    headerName: 'Filled',
                    cellClass: 'order-muted',
                    renderer: v => fmtTime(v, {fmt: 'HH:mm:ss.SSS'}),
                    width: 110
                }
            ]
        });
    }
}
